import React, { useEffect, useMemo, useState } from 'react';
import type { OwnerMailHubData, OwnerMailItem } from '../types';
import { getOwnerMailHub, updateOwnerMailItem } from '../api';
import '@/features/orders-publications/business-operations.css';

interface OwnerMailHubViewProps {
  onBack?: () => void;
  onClose?: () => void;
  /** Kept for callers; mail always opens as block7 overlay. */
  embedded?: boolean;
}

const FOLDERS = [
  { id: 'Inbox', label: 'Входящие' },
  { id: 'Spam', label: 'Спам' },
  { id: 'Sent', label: 'Отправленные' },
  { id: 'Needs Action', label: 'Требуют действия' },
  { id: 'Archived', label: 'Архив' },
] as const;

const SEMANTIC_CLASSES = [
  'Запрос на правки',
  'Рецензия',
  'Счёт / APC',
  'Принятие / публикация',
  'Приглашение',
  'Конференция',
  'Вопрос редактора',
  'Другое',
];

/**
 * Port of template `presovet/block7-potok01-owner-business.js` → mailView / mailBody.
 * Styles: business-operations.css
 */
export const OwnerMailHubView: React.FC<OwnerMailHubViewProps> = ({ onBack, onClose }) => {
  const [data, setData] = useState<OwnerMailHubData | null>(null);
  const [accountId, setAccountId] = useState('ALL');
  const [folder, setFolder] = useState('Inbox');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const loadMail = async () => {
    try {
      setLoading(true);
      const res = await getOwnerMailHub({
        account_id: accountId !== 'ALL' ? accountId : undefined,
        folder: folder !== 'All' ? folder : undefined,
      });
      setData(res);
      if (res.messages.length) {
        setSelectedId((prev) =>
          prev && res.messages.some((m) => m.id === prev) ? prev : res.messages[0].id,
        );
      } else {
        setSelectedId(null);
      }
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadMail();
  }, [accountId, folder]);

  const messages = useMemo(() => data?.messages ?? [], [data]);
  const selected: OwnerMailItem | null =
    messages.find((m) => m.id === selectedId) ?? messages[0] ?? null;

  const patchMail = async (id: string, payload: Parameters<typeof updateOwnerMailItem>[1]) => {
    try {
      const updated = await updateOwnerMailItem(id, payload);
      setData((prev) =>
        prev
          ? {
              ...prev,
              messages: prev.messages.map((m) => (m.id === updated.id ? updated : m)),
            }
          : null,
      );
      setSelectedId(updated.id);
    } catch {
      /* keep local */
    }
  };

  const accountsCount = data?.accounts_count ?? data?.accounts?.length ?? 12;
  const needsReply = data?.needs_reply_count ?? 0;
  const linked = data?.linked_to_order_count ?? 0;
  const classesCount = data?.semantic_classes_count ?? 8;
  const foldersCount = data?.mail_folders_count ?? 5;

  return (
    <div className="biz-ops-overlay open" role="dialog" aria-modal="true" aria-label="Почта владельца">
      <div className="biz-ops-top">
        <button type="button" className="biz-ops-btn" onClick={onBack}>
          ← Назад
        </button>
        <div>
          <h2>Почта владельца</h2>
          <p>12 служебных аккаунтов, классификация, связь с заказами и статус ответа</p>
        </div>
        <div className="biz-ops-top-actions">
          {onClose ? (
            <button type="button" className="biz-ops-btn" onClick={onClose}>
              Закрыть
            </button>
          ) : null}
        </div>
      </div>

      <main className="biz-ops-main">
        <div className="biz-ops-wrap">
          <div className="biz-ops-crumb">
            Бизнес-операции / <b>Почта владельца</b>
          </div>

          <div className="biz-ops-kpis">
            <div className="biz-ops-kpi">
              <b>{accountsCount}</b>
              <span>подключаемых ящиков</span>
            </div>
            <div className="biz-ops-kpi">
              <b>{needsReply}</b>
              <span>требуется ответ</span>
            </div>
            <div className="biz-ops-kpi">
              <b>{linked}</b>
              <span>связаны с заказом</span>
            </div>
            <div className="biz-ops-kpi">
              <b>{classesCount}</b>
              <span>классы писем</span>
            </div>
            <div className="biz-ops-kpi">
              <b>{foldersCount}</b>
              <span>папки почты</span>
            </div>
          </div>

          {loading && !data ? (
            <div className="biz-ops-notice">Загрузка почты…</div>
          ) : (
            <div className="biz-ops-mail-layout">
              <section className="biz-ops-mail-col">
                <div className="biz-ops-mail-head">Служебные аккаунты</div>
                <div className="biz-ops-mail-accounts">
                  <button
                    type="button"
                    className={accountId === 'ALL' ? 'active' : ''}
                    onClick={() => setAccountId('ALL')}
                  >
                    <strong>Все {accountsCount} аккаунтов</strong>
                    <small>единый рабочий поток</small>
                  </button>
                  {(data?.accounts ?? []).map((a) => (
                    <button
                      key={a.id}
                      type="button"
                      className={accountId === a.id ? 'active' : ''}
                      onClick={() => setAccountId(a.id)}
                    >
                      <strong>{a.label}</strong>
                      <small>{a.address}</small>
                    </button>
                  ))}
                </div>
              </section>

              <section className="biz-ops-mail-col">
                <div className="biz-ops-mail-head">Письма</div>
                <div className="biz-ops-folders">
                  {FOLDERS.map((f) => (
                    <button
                      key={f.id}
                      type="button"
                      className={folder === f.id ? 'active' : ''}
                      onClick={() => setFolder(f.id)}
                    >
                      {f.label}
                    </button>
                  ))}
                </div>
                <div className="biz-ops-mail-msgs">
                  {messages.length ? (
                    messages.map((m) => (
                      <button
                        key={m.id}
                        type="button"
                        className={selected?.id === m.id ? 'active' : ''}
                        onClick={() => setSelectedId(m.id)}
                      >
                        <strong>{m.subject}</strong>
                        <small>
                          {m.sender_name || m.sender_email || '—'} ·{' '}
                          {new Date(m.received_at).toLocaleString('ru-RU')} · {m.semantic_class}
                        </small>
                      </button>
                    ))
                  ) : (
                    <div className="biz-ops-notice">В этой папке для выбранного аккаунта писем нет.</div>
                  )}
                </div>
              </section>

              <section className="biz-ops-mail-col">
                {selected ? (
                  <div className="biz-ops-mail-body">
                    <h3>{selected.subject}</h3>
                    <div className="meta">
                      {selected.sender_name || selected.sender_email || '—'} ·{' '}
                      {new Date(selected.received_at).toLocaleString('ru-RU')} ·{' '}
                      {selected.account_label}
                    </div>
                    <div className="biz-ops-tags">
                      <span className="biz-ops-chip violet">{selected.semantic_class}</span>
                      {selected.is_auto_classified ? (
                        <span className="biz-ops-chip">Автоклассификация</span>
                      ) : null}
                      <span className="biz-ops-chip gold">{selected.reply_state}</span>
                      {selected.order_ref ? (
                        <span className="biz-ops-chip ok">Связь · {selected.order_ref}</span>
                      ) : null}
                    </div>
                    <div className="biz-ops-mail-text">{selected.body || selected.preview}</div>

                    <label style={{ display: 'grid', gap: 5, color: '#86a9b9', fontSize: 12 }}>
                      Классификация письма
                      <select
                        className="biz-ops-select"
                        value={selected.semantic_class}
                        onChange={(e) => void patchMail(selected.id, { semantic_class: e.target.value })}
                      >
                        {SEMANTIC_CLASSES.map((x) => (
                          <option key={x} value={x}>
                            {x}
                          </option>
                        ))}
                        {!SEMANTIC_CLASSES.includes(selected.semantic_class) ? (
                          <option value={selected.semantic_class}>{selected.semantic_class}</option>
                        ) : null}
                      </select>
                    </label>

                    <label
                      style={{ display: 'grid', gap: 5, marginTop: 8, color: '#86a9b9', fontSize: 12 }}
                    >
                      Связать с заказом
                      <select
                        className="biz-ops-select"
                        value={selected.order_ref || ''}
                        onChange={(e) =>
                          void patchMail(selected.id, {
                            order_ref: e.target.value || null,
                            order_title: e.target.value ? selected.order_title || e.target.value : null,
                          })
                        }
                      >
                        <option value="">Не связано</option>
                        {selected.order_ref ? (
                          <option value={selected.order_ref}>
                            {selected.order_title || selected.order_ref}
                          </option>
                        ) : null}
                      </select>
                    </label>

                    <div className="biz-ops-toolbar">
                      <button
                        type="button"
                        className="biz-ops-btn red"
                        onClick={() => void patchMail(selected.id, { reply_state: 'Требуется ответ' })}
                      >
                        Требуется ответ
                      </button>
                      <button
                        type="button"
                        className="biz-ops-btn gold"
                        onClick={() => void patchMail(selected.id, { reply_state: 'В работе' })}
                      >
                        В работе
                      </button>
                      <button
                        type="button"
                        className="biz-ops-btn mint"
                        onClick={() => void patchMail(selected.id, { reply_state: 'Отвечено' })}
                      >
                        Отвечено
                      </button>
                      <button
                        type="button"
                        className="biz-ops-btn"
                        onClick={() => void patchMail(selected.id, { reply_state: 'Закрыто' })}
                      >
                        Закрыто
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="biz-ops-mail-body">Выберите письмо.</div>
                )}
              </section>
            </div>
          )}

          <div className="biz-ops-source">
            <b>Подключение:</b> почтовые коннекторы, безопасное хранение токенов, синхронизация,
            цепочки писем и отправка ответов выполняются на сервере.
          </div>
        </div>
      </main>
    </div>
  );
};
